/**
 * The Account page's membership card — the web counterpart of the native one
 * (`mobile/src/navigation/screens/account/components/MembershipCard.tsx`), ELARA-1014.
 *
 * Styled after a physical membership card: the ISO/IEC 7810 ID-1 aspect ratio, a camel-950 surface
 * (a fixed brand colour rather than a themed role — a physical card reads the same in any light, so
 * this one does not flip with the theme), the brand mark bled off the top-right corner, and the
 * cardholder's name over "Member since" in the bottom-left.
 *
 * Deliberately static. The native card's shine tracks the phone's gyroscope, which has no honest
 * web equivalent — a mouse-following glow would be a different effect wearing the same clothes, and
 * a looping animation would draw the eye to a surface that is not the point of the page. A soft
 * fixed highlight gives it the same depth without inventing motion.
 */

import { Logo } from '#ui/index.js';

/** ISO/IEC 7810 ID-1 (85.60 × 53.98mm) — the credit/membership card ratio the native card uses. */
const CARD_ASPECT_RATIO = 85.6 / 53.98;

const MARK_SIZE = 180;
/** Shared by both copies of the mark: the shine only reads as foil while it lines up exactly. */
const MARK_POSITION = '-top-8 -right-8';
/**
 * Where the foil catches. A soft off-centre blob rather than a hard streak — the same falloff the
 * native card builds from SVG gradient stops, which is what makes it read as light on metal instead
 * of a highlight pasted on top. Positioned up and left within the mark, so the lit side faces the
 * card's ambient gradient.
 */
const FOIL_MASK =
  'radial-gradient(65% 65% at 30% 22%, #000 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.18) 65%, transparent 82%)';

export interface MembershipCardProps {
  name: string;
  /** Pre-formatted "member since" date (e.g. "July 2026"); omitted when unknown. */
  memberSince?: string;
}

export function MembershipCard({ name, memberSince }: MembershipCardProps) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-lg bg-camel-950 shadow-sm"
      style={{ aspectRatio: CARD_ASPECT_RATIO }}
    >
      {/* The mark, tone-on-tone: present, but never competing with the name. Bled off the corner
          like the Home `ImageCard` placeholder mark. */}
      <span className={`pointer-events-none absolute opacity-10 ${MARK_POSITION}`} aria-hidden>
        <Logo variant="symbol" height={MARK_SIZE} className="text-white" />
      </span>

      {/* Ambient light on the surface, from the top-left. */}
      <span
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_25%_0%,rgba(255,255,255,0.10),transparent_40%)]"
        aria-hidden
      />

      {/* The foil shine: a second copy of the mark, bright, revealed only where the mask lets it
          through — so a soft patch of the symbol catches the light while the rest stays tone-on-tone.
          This is the web reading of the native card's masked logo sweep, which gives the mark its
          silvery lift. There it drifts with the gyroscope; here the light source is fixed, placed to
          agree with the ambient gradient above rather than pointing somewhere of its own. */}
      <span
        className={`pointer-events-none absolute ${MARK_POSITION}`}
        style={{ maskImage: FOIL_MASK, WebkitMaskImage: FOIL_MASK }}
        aria-hidden
      >
        <Logo variant="symbol" height={MARK_SIZE} className="text-white/45" />
      </span>

      <div className="absolute bottom-5 left-5 right-5 flex flex-col">
        {/* Fixed light text: the surface does not theme, so these stay constant in both schemes. */}
        <div className="truncate font-medium tracking-wide text-white">{name}</div>
        {memberSince && <div className="truncate text-xs text-white/70">Member since {memberSince}</div>}
      </div>
    </div>
  );
}
